import { GATEWAY_API_URL } from '@admin-panel/config/env';
import { axiosInstance } from '@admin-panel/providers/lib/clients/axios';
import { canGetFCMToken, getFCMToken, initializeNotificationsApp } from '@lyskraft/firebase';

type FirebaseConfig = {
  apiKey?: string;
  authDomain?: string;
  projectId?: string;
  storageBucket?: string;
  messagingSenderId?: string;
  appId?: string;
  measurementId?: string;
  vapidKey?: string;
};

type CompleteFirebaseConfig = Required<FirebaseConfig>;

export type RiderPushResult =
  | { status: 'enabled' }
  | { status: 'unsupported'; reason: string }
  | { status: 'denied' }
  | { status: 'not_configured' };

const firebaseConfig: FirebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_NOTIFICATIONS_API_KEY as string,
  authDomain: import.meta.env.VITE_FIREBASE_NOTIFICATIONS_AUTH_DOMAIN as string,
  projectId: import.meta.env.VITE_FIREBASE_NOTIFICATIONS_PROJECT_ID as string,
  storageBucket: import.meta.env.VITE_FIREBASE_NOTIFICATIONS_STORAGE_BUCKET as string,
  messagingSenderId: import.meta.env.VITE_FIREBASE_NOTIFICATIONS_MESSAGING_SENDER_ID as string,
  appId: import.meta.env.VITE_FIREBASE_NOTIFICATIONS_APP_ID as string,
  measurementId: import.meta.env.VITE_FIREBASE_NOTIFICATIONS_MEASUREMENT_ID as string,
  vapidKey: import.meta.env.VITE_FIREBASE_NOTIFICATIONS_VAPID_KEY as string,
};

const hasFirebaseConfig = (config: FirebaseConfig) =>
  Boolean(
    config.apiKey &&
      config.authDomain &&
      config.projectId &&
      config.messagingSenderId &&
      config.appId &&
      config.vapidKey,
  );

const getCompleteFirebaseConfig = (): CompleteFirebaseConfig | null =>
  hasFirebaseConfig(firebaseConfig) ? (firebaseConfig as CompleteFirebaseConfig) : null;

let initialized = false;

export const initializeRiderPushNotifications = () => {
  const completeConfig = getCompleteFirebaseConfig();
  if (initialized || !completeConfig) {
    return Boolean(completeConfig);
  }

  initializeNotificationsApp({ config: completeConfig });
  initialized = true;
  return true;
};

export const enableRiderPushNotifications = async (): Promise<RiderPushResult> => {
  if (!initializeRiderPushNotifications()) {
    return { status: 'not_configured' };
  }

  if (!('Notification' in window)) {
    return { status: 'unsupported', reason: 'This browser does not support notifications.' };
  }

  const permission =
    Notification.permission === 'granted'
      ? 'granted'
      : await Notification.requestPermission();

  if (permission !== 'granted') {
    return { status: 'denied' };
  }

  if (!(await canGetFCMToken())) {
    return {
      status: 'unsupported',
      reason: 'Install this app on the home screen to enable ride alerts.',
    };
  }

  if ('serviceWorker' in navigator) {
    await navigator.serviceWorker.ready;
  }

  const token = await getFCMToken();
  if (!token) {
    return { status: 'unsupported', reason: 'Notification token could not be generated.' };
  }

  await axiosInstance.post(`${GATEWAY_API_URL}/notification-token/upsert`, {
    platform: 'FIREBASE',
    token,
  });

  return { status: 'enabled' };
};
